'use client';

import * as React from 'react';
import { User } from 'lucide-react';
import type { CityCode } from '@loci/city-editions';

import { GlassControl } from '@/components/atoms/GlassControl';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/primitives/popover';
import { cn } from '@/components/atoms/primitives/utils';
import { GLOBAL_NAV_ACCENT_VARS } from '@/components/molecules/CityCoordStrip/GlobalNavCoordStrip';
import {
  ACCOUNT_AFFORDANCE_ARIA_LABEL,
  ACCOUNT_AFFORDANCE_CONTROL,
  ACCOUNT_CHOOSER_ARIA_LABEL,
  CHOOSER_SIGN_IN_LINK,
  CHOOSER_SIGN_UP_LINK,
  FOCUS_RING,
  cityNavSignUpHref,
  isCrossOrigin,
} from '@/components/molecules/CityHeader/cityHeaderShared';
import { useCitySignInHref } from '@/hooks/useCitySignInHref';
import { useCitySignUpHref } from '@/hooks/useCitySignUpHref';
import { useCityIsStaging } from '@/lib/city-context.client';

/**
 * CityAccountChooser — the anon account control on the marketing-city header
 * (FRE-1389, DS 1.9.1 `buildAuth` / PL-05).
 *
 * The single account icon (WEB-617 / DES-59 B) opens a compact sign-in /
 * sign-up choice at **every width**: the amber "Sign up — build your
 * footprint" CTA first (signup stays the dominant path; `from=city-nav` keeps
 * the DES-59 rollback metric), then a ghost "Sign in". This replaces the
 * direct signup link, whose only Sign in path was the sitemap menu — which
 * the DS hides ≥721px (PL-02), leaving a returning desktop visitor no way to
 * sign in from the header.
 *
 * Both links carry `next=<this city page>` so auth returns the visitor here.
 * Island pattern: `'use client'`, mounted from the server-default `CityHeader`.
 * Built on the Radix Popover (Escape / outside click close, focus returns to
 * the trigger, `aria-expanded` on the button) like `WayfinderSheet`; ≤720px the
 * panel pins under the chrome (`styles/overrides/chrome.css`).
 */

export interface CityAccountChooserProps {
  /** City code for the return URL + staging host resolution. */
  readonly cityCode: CityCode;
  /** Signup destination override. Defaults to cross-host signup with `next`. */
  readonly signUpUrl?: string;
  /** Sign-in destination override. Defaults to cross-host login with `next` + `force`. */
  readonly signInUrl?: string;
  /**
   * Request-host staging flag (WEB-776), threaded by `CityHeader`. Wins over
   * the `CityStagingProvider` context, which is the fallback.
   */
  readonly isStaging?: boolean;
  /** Uncontrolled initial open — Storybook / tests only. */
  readonly defaultOpen?: boolean;
  /** Extra classes on the trigger. */
  readonly className?: string;
}

export function CityAccountChooser({
  cityCode,
  signUpUrl,
  signInUrl,
  isStaging: isStagingProp,
  defaultOpen = false,
  className,
}: CityAccountChooserProps): React.ReactElement {
  const contextIsStaging = useCityIsStaging();
  const isStaging = isStagingProp ?? contextIsStaging;
  const signUpHref = cityNavSignUpHref(useCitySignUpHref(cityCode, signUpUrl, { isStaging }));
  const signInHref = useCitySignInHref(cityCode, signInUrl, { isStaging });
  const [open, setOpen] = React.useState(defaultOpen);
  // Cross-origin auth opens in a new tab (same convention as the sitemap's
  // auth block), so close the chooser behind it.
  const close = React.useCallback(() => setOpen(false), []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <GlassControl
          size="nav"
          aria-label={ACCOUNT_AFFORDANCE_ARIA_LABEL}
          data-testid="city-header-account"
          data-auth="anon"
          className={cn(ACCOUNT_AFFORDANCE_CONTROL, className)}
        >
          <User aria-hidden="true" className="size-4 shrink-0" strokeWidth={2} />
        </GlassControl>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={6}
        aria-label={ACCOUNT_CHOOSER_ARIA_LABEL}
        data-testid="city-account-chooser"
        style={GLOBAL_NAV_ACCENT_VARS.city}
        className={cn(
          'nav-menu-sheet-surface flex w-72 max-w-[calc(100vw-24px)] flex-col gap-2.5',
          'rounded-ds-xl border-border-strong p-3.5 text-fg-1',
          // DS `--gn-sheet-bg` lift: drop shadow + 1px inner highlight (same as WayfinderSheet).
          'shadow-[0_32px_64px_rgba(0,0,0,0.55),0_0_0_1px_rgba(255,255,255,0.04)_inset]',
          'animate-loci-sheet-in',
        )}
      >
        <p className="font-mono text-micro uppercase tracking-eyebrow text-gn-accent-glow">
          Your LOCI
        </p>
        <a
          href={signUpHref}
          onClick={close}
          data-testid="city-account-chooser-sign-up"
          className={cn(CHOOSER_SIGN_UP_LINK, FOCUS_RING)}
          {...(isCrossOrigin(signUpHref) ? { target: '_blank', rel: 'noreferrer' } : {})}
        >
          Sign up. Build your footprint
        </a>
        <a
          href={signInHref}
          onClick={close}
          data-testid="city-account-chooser-sign-in"
          className={cn(CHOOSER_SIGN_IN_LINK, FOCUS_RING)}
          {...(isCrossOrigin(signInHref) ? { target: '_blank', rel: 'noreferrer' } : {})}
        >
          Sign in
        </a>
      </PopoverContent>
    </Popover>
  );
}
